/* global $ */
import React, { useState, useEffect, useCallback } from 'react';
import DataTable from 'react-data-table-component';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  MASTER_ENTRY_PAGES,
  REGISTRATION_PAGES,
  EVALUATION_PAGES,
  DOCUMENT_PAGES,
  REPORT_PAGES,
} from '../components/Sidebar';

const API_BASE = 'https://api.eazyworldehsengineering.com/api/admin';

const ROLE_ADMIN = 1;
const ROLE_STAFF = 2;

const PERMISSION_GROUPS = [
  { label: 'Master Entry', pages: MASTER_ENTRY_PAGES },
  { label: 'Registration', pages: REGISTRATION_PAGES },
  { label: 'Evaluation', pages: EVALUATION_PAGES },
  { label: 'Documents', pages: DOCUMENT_PAGES },
  { label: 'Reports', pages: REPORT_PAGES },
];

const emptyRecord = {
  username: '',
  password: '',
  emailaddress: '',
  first_name: '',
  last_name: '',
  role: ROLE_STAFF,
  status: 'active',
  permissions: null, // null = full access
};

const UserManagement = () => {
  const [data, setData] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [newRecord, setNewRecord] = useState(emptyRecord);
  const [searchText, setSearchText] = useState('');
  const currentUserId = Number(localStorage.getItem('user_id'));

  useEffect(() => {
    const handleModalClose = () => {
      $('body').removeClass('modal-open');
      $('.modal-backdrop').remove();
      $('body').css({ 'padding-right': '', overflow: '' });
      $('.content-wrapper').css('min-height', 'calc(100vh - 114px)');
    };
    $('#editRowModal').on('hidden.bs.modal', handleModalClose);
    $('#addRowModal').on('hidden.bs.modal', handleModalClose);
    return () => {
      $('#editRowModal').off('hidden.bs.modal', handleModalClose);
      $('#addRowModal').off('hidden.bs.modal', handleModalClose);
    };
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE}/`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInputChange = (event) => {
    const { id, value } = event.target;
    setNewRecord((prev) => ({ ...prev, [id]: id === 'role' ? Number(value) : value }));
  };

  const togglePermission = (path) => {
    setNewRecord((prev) => {
      const current = prev.permissions || [];
      const next = current.includes(path)
        ? current.filter((p) => p !== path)
        : [...current, path];
      return { ...prev, permissions: next };
    });
  };

  const toggleFullAccess = (fullAccess) => {
    setNewRecord((prev) => ({ ...prev, permissions: fullAccess ? null : [] }));
  };

  const handleAddButtonClick = () => {
    setNewRecord(emptyRecord);
    $('#addRowModal').modal('show');
  };

  const handleEdit = (row) => {
    setSelectedRow(row);
    setNewRecord({
      username: row.username,
      password: '',
      emailaddress: row.emailaddress,
      first_name: row.first_name,
      last_name: row.last_name,
      role: row.role,
      status: row.status,
      permissions: row.permissions || null,
    });
    $('#editRowModal').modal('show');
  };

  const handleCreateSubmit = async (event) => {
    event.preventDefault();
    try {
      await axios.post(`${API_BASE}/`, {
        username: newRecord.username,
        password: newRecord.password,
        emailaddress: newRecord.emailaddress,
        first_name: newRecord.first_name,
        last_name: newRecord.last_name,
        role: newRecord.role,
        status: newRecord.status,
        permissions: newRecord.role === ROLE_ADMIN ? null : newRecord.permissions,
      });
      $('#addRowModal').modal('hide');
      await fetchData();
      Swal.fire('Created!', 'The user has been created.', 'success');
    } catch (error) {
      Swal.fire('Error!', error.response?.data?.error || 'There was an issue creating the user.', 'error');
    }
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    if (!selectedRow) return;
    try {
      await axios.put(`${API_BASE}/${selectedRow.id}`, {
        username: newRecord.username,
        emailaddress: newRecord.emailaddress,
        firstname: newRecord.first_name,
        lastname: newRecord.last_name,
        role: newRecord.role,
        status: newRecord.status,
        permissions: newRecord.role === ROLE_ADMIN ? null : newRecord.permissions,
      });
      $('#editRowModal').modal('hide');
      await fetchData();
      Swal.fire('Saved!', 'The user has been updated.', 'success');
    } catch (error) {
      Swal.fire('Error!', error.response?.data?.error || 'There was an issue updating the user.', 'error');
    }
  };

  const handleDelete = useCallback(async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    });
    if (result.isConfirmed) {
      try {
        await axios.delete(`${API_BASE}/${id}`);
        await fetchData();
        Swal.fire('Deleted!', 'The user has been deleted.', 'success');
      } catch (error) {
        Swal.fire('Error!', error.response?.data?.error || 'There was an issue deleting the user.', 'error');
      }
    }
  }, [fetchData]);

  const handleResetPassword = useCallback(async (row) => {
    const result = await Swal.fire({
      title: `Reset password for ${row.username}?`,
      text: 'A new random password will replace their current one immediately.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, reset it!',
    });
    if (!result.isConfirmed) return;
    try {
      const response = await axios.post(`${API_BASE}/${row.id}/reset-password`);
      const { new_password } = response.data;
      await Swal.fire({
        title: 'Password Reset',
        html: `New temporary password for <b>${row.username}</b>:<br/><br/><code style="font-size:1.2rem">${new_password}</code><br/><br/>Share this with the user - they can change it after logging in via Change Password.`,
        icon: 'success',
      });
    } catch (error) {
      Swal.fire('Error!', error.response?.data?.error || 'There was an issue resetting the password.', 'error');
    }
  }, []);

  const filteredData = data.filter((item) =>
    item.username.toLowerCase().includes(searchText.toLowerCase()) ||
    item.emailaddress.toLowerCase().includes(searchText.toLowerCase()) ||
    `${item.first_name} ${item.last_name}`.toLowerCase().includes(searchText.toLowerCase())
  );

  const roleBadge = (role) => (
    <span className={`badge ${role === ROLE_ADMIN ? 'badge-primary' : 'badge-secondary'}`}>
      {role === ROLE_ADMIN ? 'Admin' : 'Staff'}
    </span>
  );

  const statusBadge = (status) => (
    <span className={`badge ${status === 'active' ? 'badge-success' : 'badge-warning'}`}>{status}</span>
  );

  const columns = [
    { name: 'Username', selector: (row) => row.username, sortable: true },
    { name: 'Name', selector: (row) => `${row.first_name} ${row.last_name}`, sortable: true },
    { name: 'Email', selector: (row) => row.emailaddress, sortable: true },
    { name: 'Role', cell: (row) => roleBadge(row.role), width: '110px' },
    { name: 'Status', cell: (row) => statusBadge(row.status), width: '110px' },
    { name: 'Access', cell: (row) => (row.role === ROLE_ADMIN ? 'Full (Admin)' : row.permissions ? `${row.permissions.length} page(s)` : 'Full access') },
    {
      name: 'Actions',
      cell: (row) => (
        <div>
          <button onClick={() => handleEdit(row)} className="btn btn-primary btn-sm"><i className="fa fa-edit"></i></button>
          <button onClick={() => handleResetPassword(row)} className="btn btn-warning btn-sm"><i className="fa fa-key"></i></button>
          <button
            onClick={() => handleDelete(row.id)}
            className="btn btn-danger btn-sm"
            disabled={row.id === currentUserId}
            title={row.id === currentUserId ? "You can't delete your own account" : 'Delete'}
          >
            <i className="fa fa-times"></i>
          </button>
        </div>
      ),
      width: '160px',
    },
  ];

  const PermissionFields = () => (
    <>
      {newRecord.role === ROLE_STAFF && (
        <div className="form-group col-md-12">
          <div className="form-check mb-2">
            <input
              type="checkbox"
              className="form-check-input"
              id="fullAccessToggle"
              checked={newRecord.permissions === null}
              onChange={(e) => toggleFullAccess(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="fullAccessToggle">
              Full access (no page restrictions)
            </label>
          </div>
          {newRecord.permissions !== null && (
            <div className="row">
              {PERMISSION_GROUPS.map((group) => (
                <div className="col-md-4 mb-2" key={group.label}>
                  <strong className="d-block mb-1">{group.label}</strong>
                  {group.pages.map((page) => (
                    <div className="form-check" key={page.path}>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`perm-${page.path}`}
                        checked={(newRecord.permissions || []).includes(page.path)}
                        onChange={() => togglePermission(page.path)}
                      />
                      <label className="form-check-label" htmlFor={`perm-${page.path}`}>
                        {page.label}
                      </label>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );

  return (
    <div className="content-wrapper">
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0 text-dark">User Management</h1>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item"><a href="/">Home</a></li>
                <li className="breadcrumb-item active">User Management</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="row mb-3 px-3">
        <div className="col text-right">
          <button className="btn btn-primary" onClick={handleAddButtonClick} data-bs-toggle="modal" data-bs-target="#addRowModal">
            <i className="fa fa-plus mr-1"></i> Add New
          </button>
        </div>
      </div>

      <section className="content px-3">
        <div className="card">
          <div className="card-body">
            <input
              type="text"
              placeholder="Search..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="form-control mb-3"
            />
            <DataTable columns={columns} data={filteredData} pagination />

            {/* Add Modal */}
            <div className="modal fade" id="addRowModal">
              <div className="modal-dialog modal-lg">
                <div className="modal-content">
                  <form onSubmit={handleCreateSubmit}>
                    <div className="modal-header bg-success">
                      <h4 className="modal-title text-white">Add User</h4>
                      <button type="button" className="close text-white" data-dismiss="modal"><span>&times;</span></button>
                    </div>
                    <div className="modal-body">
                      <div className="row">
                        <div className="col-md-6 form-group">
                          <label>Username</label>
                          <input id="username" type="text" className="form-control" value={newRecord.username} onChange={handleInputChange} required />
                        </div>
                        <div className="col-md-6 form-group">
                          <label>Password</label>
                          <input id="password" type="text" className="form-control" value={newRecord.password} onChange={handleInputChange} required />
                        </div>
                        <div className="col-md-6 form-group">
                          <label>Email</label>
                          <input id="emailaddress" type="email" className="form-control" value={newRecord.emailaddress} onChange={handleInputChange} required />
                        </div>
                        <div className="col-md-6 form-group">
                          <label>Role</label>
                          <select id="role" className="form-control" value={newRecord.role} onChange={handleInputChange}>
                            <option value={ROLE_ADMIN}>Admin</option>
                            <option value={ROLE_STAFF}>Staff</option>
                          </select>
                        </div>
                        <div className="col-md-6 form-group">
                          <label>First Name</label>
                          <input id="first_name" type="text" className="form-control" value={newRecord.first_name} onChange={handleInputChange} required />
                        </div>
                        <div className="col-md-6 form-group">
                          <label>Last Name</label>
                          <input id="last_name" type="text" className="form-control" value={newRecord.last_name} onChange={handleInputChange} required />
                        </div>
                        <div className="col-md-6 form-group">
                          <label>Status</label>
                          <select id="status" className="form-control" value={newRecord.status} onChange={handleInputChange}>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                            <option value="pending">Pending</option>
                          </select>
                        </div>
                        <PermissionFields />
                      </div>
                    </div>
                    <div className="modal-footer justify-content-between">
                      <button type="button" className="btn btn-secondary" data-dismiss="modal">Close</button>
                      <button type="submit" className="btn btn-success">Add</button>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            {/* Edit Modal */}
            <div className="modal fade" id="editRowModal">
              <div className="modal-dialog modal-lg">
                <div className="modal-content">
                  <form onSubmit={handleFormSubmit}>
                    <div className="modal-header bg-primary">
                      <h4 className="modal-title text-white">Edit User</h4>
                      <button type="button" className="close text-white" data-dismiss="modal"><span>&times;</span></button>
                    </div>
                    <div className="modal-body">
                      <div className="row">
                        <div className="col-md-6 form-group">
                          <label>Username</label>
                          <input id="username" type="text" className="form-control" value={newRecord.username} onChange={handleInputChange} />
                        </div>
                        <div className="col-md-6 form-group">
                          <label>Email</label>
                          <input id="emailaddress" type="email" className="form-control" value={newRecord.emailaddress} onChange={handleInputChange} />
                        </div>
                        <div className="col-md-6 form-group">
                          <label>Role</label>
                          <select id="role" className="form-control" value={newRecord.role} onChange={handleInputChange}>
                            <option value={ROLE_ADMIN}>Admin</option>
                            <option value={ROLE_STAFF}>Staff</option>
                          </select>
                        </div>
                        <div className="col-md-6 form-group">
                          <label>First Name</label>
                          <input id="first_name" type="text" className="form-control" value={newRecord.first_name} onChange={handleInputChange} />
                        </div>
                        <div className="col-md-6 form-group">
                          <label>Last Name</label>
                          <input id="last_name" type="text" className="form-control" value={newRecord.last_name} onChange={handleInputChange} />
                        </div>
                        <div className="col-md-6 form-group">
                          <label>Status</label>
                          <select id="status" className="form-control" value={newRecord.status} onChange={handleInputChange}>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                            <option value="pending">Pending</option>
                          </select>
                        </div>
                        <PermissionFields />
                      </div>
                    </div>
                    <div className="modal-footer justify-content-between">
                      <button type="button" className="btn btn-secondary" data-dismiss="modal">Close</button>
                      <button type="submit" className="btn btn-primary">Save Changes</button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UserManagement;
